import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ds/Button";
import { Card } from "@/components/ds/Card";

/**
 * Le filet sous l'application entière.
 *
 * Sans lui, une exception dans un rendu — un champ absent d'une réponse, une date
 * mal formée — démonte l'arbre React et laisse une PAGE BLANCHE. Rien à cliquer,
 * rien à lire, et une entreprise qui ne reviendra pas : elle visite cet espace
 * trois fois par an, elle n'a aucune raison de croire à un incident passager.
 *
 * ⚠️ Un `ErrorBoundary` n'attrape QUE les erreurs de rendu, de cycle de vie et de
 * constructeur, dans l'arbre en dessous de lui. Les rejets de promesse d'un
 * gestionnaire d'événement lui échappent — ceux-là sont la charge de `fetchJson`
 * et des états d'erreur de chaque écran.
 */

type Props = { children: ReactNode };
type State = { crashed: boolean };

export class ErrorBoundary extends Component<Props, State> {
	state: State = { crashed: false };

	static getDerivedStateFromError(): State {
		return { crashed: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		// La console est le seul journal du navigateur, et il n'y a pas de collecteur
		// d'erreurs front sur ce projet. Une trace sans destinataire vaut mieux qu'une
		// page blanche sans trace : c'est par là que commencera l'enquête.
		console.error("Écran en échec", error, info.componentStack);
	}

	render(): ReactNode {
		if (!this.state.crashed) return this.props.children;

		// Le filet couvre les deux versants. « Écrivez à votre école » n'a pas de sens
		// pour le référent de cette école : son texte dit seulement que rien n'est perdu.
		const ecole = window.location.pathname.startsWith("/ecole");

		return (
			<div className="flex min-h-screen items-center justify-center bg-mg-page px-6">
				<Card size="large" className="w-full max-w-[420px] text-center">
					<h1 className="t-h3 text-mg-heading">Cette page n'a pas pu s'afficher</h1>
					<p className="t-body mt-2 text-mg-secondary">
						{ecole
							? "Rechargez la page. Vos offres, vos profils envoyés et vos réglages sont intacts."
							: "Rechargez la page. Si cela se reproduit, écrivez à votre école : votre demande et vos profils sont intacts."}
					</p>
					<Button
						tone="primary"
						full
						className="mt-6"
						onClick={() => {
							window.location.reload();
						}}
					>
						Recharger la page
					</Button>
				</Card>
			</div>
		);
	}
}
